import { lazy } from 'react';
import {
  decisionTaggingIcon,
  generalIcon,
  organizationViewIcon,
  personalIcon,
  SearchPaymentLocatorIcon,
  shiftManagerIcon
} from '@tools/ui/assets';
import { InboxArea } from '@tools/ui/enums';
import type { AppRouter } from '@tools/ui/types';
import { theme } from '@tools/ui-utils/theme.util';
import { accountViewRoutes } from '../pages/sherlock/routes/accountView.routes';
import { generalRoutes } from '../pages/sherlock/routes/general.routes';
import { organizationTasksRoutes } from '../pages/sherlock/routes/organizationTasks.routes';
import { personalRoutes } from '../pages/sherlock/routes/personal.routes';
import { projectRoutes } from '../pages/sherlock/routes/project.routes';
import { projectTaskRoutes } from '../pages/sherlock/routes/projectTask.routes';
import { supplierViewRoutes } from '../pages/sherlock/routes/supplierView.routes';
import { shiftManagerRoutes } from './shift-manager';

const prefixPath = '/sherlock';

const ShiftManager = lazy(
  () =>
    import(
      /* webpackChunkName: "SherlockRoutes" */ '../pages/sherlock/shift-manager'
    )
);

const Sherlock = lazy(
  () => import(/* webpackChunkName: "SherlockRoutes" */ '../pages/sherlock')
);

const SherlockGeneric = lazy(
  () =>
    import(
      /* webpackChunkName: "SherlockRoutes" */ '../pages/sherlock/genericInbox/sherlockGeneric'
    )
);

const SherlockProject = lazy(
  () =>
    import(
      /* webpackChunkName: "SherlockRoutes" */ '../pages/sherlock/projects/sherlockProject'
    )
);

const PaymentLocator = lazy(
  () =>
    import(
      /* webpackChunkName: "SherlockRoutes" */ '../pages/sherlock/payment-locator'
    )
);

const AccountView = lazy(
  () =>
    import(
      /* webpackChunkName: "SherlockRoutes" */ '../pages/sherlock/entityView/account-view'
    )
);

const SupplierView = lazy(
  () =>
    import(
      /* webpackChunkName: "SherlockRoutes" */ '../pages/sherlock/entityView/supplier-view'
    )
);

const EscalationTasksContainer = lazy(
  () =>
    import(
      /* webpackChunkName: "SherlockRoutes" */ '../pages/sherlock/escalations'
    )
);

export const sherlockRoutes: AppRouter = {
  modules: [
    {
      redirectFrom: [`${prefixPath}/`, `${prefixPath}/*`]
    },
    {
      name: 'Shift Manager',
      path: `${prefixPath}/shift-manager`,
      icon: shiftManagerIcon,
      exact: false,
      component: ShiftManager,
      pages: shiftManagerRoutes
    },
    {
      name: 'Personal',
      displayName: 'My Payments',
      path: `${prefixPath}/personal`,
      icon: personalIcon,
      exact: false,
      component: Sherlock,
      pages: personalRoutes
    },
    {
      name: 'General',
      displayName: 'All Payments',
      path: `${prefixPath}/general`,
      icon: generalIcon,
      exact: false,
      component: Sherlock,
      pages: generalRoutes
    },
    {
      name: 'Organization view',
      inboxArea: InboxArea.organization,
      path: `${prefixPath}/organization`,
      exact: false,
      icon: organizationViewIcon,
      component: SherlockGeneric,
      pages: organizationTasksRoutes
    },
    {
      name: 'Escalation',
      displayName: 'Payments of Escalation',
      path: `${prefixPath}/escalation/:escalationId`,
      icon: generalIcon,
      exact: true,
      hideFromMenu: true,
      component: EscalationTasksContainer,
      pages: []
    },
    {
      name: 'projects',
      displayName: 'Projects',
      icon: decisionTaggingIcon,
      path: `${prefixPath}/projects`,
      component: SherlockProject,
      exact: false,
      pages: projectRoutes
    },
    {
      name: 'tagging',
      inboxArea: InboxArea.tagging,
      displayName: 'Project Task',
      path: `${prefixPath}/tagging/project`,
      exact: true,
      component: SherlockGeneric,
      pages: projectTaskRoutes,
      hideFromMenu: true
    },
    {
      name: 'tagging/project',
      inboxArea: InboxArea.tagging,
      displayName: 'Project Task',
      path: `${prefixPath}/tagging/project/:projectId`,
      exact: false,
      component: SherlockGeneric,
      pages: projectTaskRoutes,
      hideFromMenu: true
    },
    {
      name: 'Payment Locator',
      path: `${prefixPath}/payment-locator`,
      icon: SearchPaymentLocatorIcon,
      exact: true,
      component: PaymentLocator,
      pages: []
    },
    {
      name: 'Account view',
      path: `${prefixPath}/account-view`,
      exact: true,
      component: AccountView,
      pages: accountViewRoutes,
      hideFromMenu: true
    },
    {
      name: 'Account view by id',
      path: `${prefixPath}/account-view/:accountId`,
      exact: false,
      component: AccountView,
      pages: accountViewRoutes,
      hideFromMenu: true,
      backgroundColor: theme.colors.accountReview.background
    },
    {
      name: 'Supplier view',
      path: `${prefixPath}/supplier-view`,
      exact: true,
      component: SupplierView,
      pages: supplierViewRoutes,
      hideFromMenu: true
    },
    {
      name: 'Supplier view by id and type',
      path: `${prefixPath}/supplier-view/:supplierId/:supplierType`,
      exact: false,
      component: SupplierView,
      pages: supplierViewRoutes,
      hideFromMenu: true,
      backgroundColor: theme.colors.accountReview.background
    }
  ]
};
